import { formatDateLong } from '@/utils/datePresentation';
import { buildInvoiceDetailHeaderActions, sumTaxItemsByDirection } from '@/composables/invoices/detail/helpers';

export const invoiceDetailComputed = {
  invoiceId() {
    return Number(this.$route?.params?.invoiceId || 0) || 0;
  },
  headerTitle() {
    const serial = String(this.invoiceData?.serial || '').trim();
    const folio = String(this.invoiceData?.folio || '').trim();
    const serialFolio = `${serial}${folio}`.trim();
    return serialFolio !== '' ? serialFolio : String(this.text.detail.page.title);
  },
  headerActions() {
    return buildInvoiceDetailHeaderActions(this.text.detail.headerActions);
  },
  primaryHeaderActions() {
    const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
    return actions.filter((action) => {
      return ['pay', 'downloadXml', 'send'].includes(String(action?.key || ''));
    });
  },
  secondaryHeaderActions() {
    const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
    const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));
    return actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
  },
  invoiceInfo() {
    const serialFallback = this.serialOptions[0]?.value || 0;

    return {
      date: formatDateLong(this.invoiceData?.date, { fallback: String(this.text.detail.page.dateFallback) }),
      rawDate: String(this.invoiceData?.date || '').trim(),
      series: String(this.invoiceData?.serial || '').trim(),
      folio: String(this.invoiceData?.folio || '').trim(),
      uuid: String(this.invoiceData?.uuid || '').trim(),
      currency: String(this.invoiceData?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchangeRate: this.invoiceData?.exchange_rate ?? this.invoiceData?.exchangeRate ?? '',
      serialId: this.invoiceData?.serialId || serialFallback,
      paymentMethod: String(this.invoiceData?.paymentMethod || '').trim(),
      paymentForm: String(this.invoiceData?.paymentForm || '').trim(),
    };
  },
  issuer() {
    return this.invoiceData?.issuer || {
      businessName: '-',
      rfc: '-',
      taxRegime: '-',
      fiscalResidence: '-',
    };
  },
  selectedReceiver() {
    return this.invoiceData?.selectedReceiver || null;
  },
  concepts() {
    return Array.isArray(this.invoiceData?.concepts) ? this.invoiceData.concepts : [];
  },
  comments() {
    return String(this.invoiceData?.comments || '').trim();
  },
  currency() {
    return String(this.invoiceData?.currency || 'MXN').trim().toUpperCase() || 'MXN';
  },
  paymentMethodLabel() {
    return String(this.invoiceData?.paymentMethodLabel || '').trim();
  },
  paymentFormLabel() {
    return String(this.invoiceData?.paymentFormLabel || '').trim();
  },
  invoiceIsCanceled() {
    const canceledFlag = Number(this.invoiceData?.canceled || 0);
    if (canceledFlag === 1) {
      return true;
    }

    const status = String(this.invoiceData?.status || this.invoiceData?.status_key || '').trim().toLowerCase();
    return ['canceled', 'cancelada'].includes(status);
  },
  invoiceStatusLabel() {
    const labels = this.text.detail.status;
    return this.invoiceIsCanceled
      ? String(labels.canceled)
      : String(labels.active);
  },
  invoiceFolioBadge() {
    const serial = String(this.invoiceData?.serial || '').trim();
    const folio = String(this.invoiceData?.folio || '').trim();
    const serialFolio = `${serial}${folio}`.trim();
    return serialFolio !== '' ? serialFolio : '-';
  },
  invoiceCustomerName() {
    const apiCustomerName = String(
      this.invoiceData?.customer_name
      || this.invoiceData?.customerName
      || this.invoiceData?.customer
      || ''
    ).trim();

    if (apiCustomerName !== '') {
      return apiCustomerName;
    }

    return String(
      this.selectedReceiver?.meta?.clientName
      || this.selectedReceiver?.clientName
      || this.selectedReceiver?.meta?.name
      || this.selectedReceiver?.name
      || this.selectedReceiver?.label
      || this.selectedReceiver?.meta?.businessName
      || this.selectedReceiver?.businessName
      || this.text.detail.page.customerPending
    ).trim() || String(this.text.detail.page.customerPending);
  },
  subtotal() {
    const payloadSubtotal = Number(this.invoiceData?.subtotal || 0);
    if (Number.isFinite(payloadSubtotal) && payloadSubtotal > 0) {
      return payloadSubtotal;
    }

    return this.concepts.reduce((sum, concept) => {
      return sum + ((Number(concept?.qty || 0) * Number(concept?.unitPrice || 0)) || 0);
    }, 0);
  },
  discountAmount() {
    const payloadDiscount = Number(this.invoiceData?.discounts || 0);
    if (Number.isFinite(payloadDiscount) && payloadDiscount > 0) {
      return payloadDiscount;
    }

    return 0;
  },
  transferredTaxAmount() {
    const taxes = this.invoiceData?.taxes;
    if (taxes && Object.prototype.hasOwnProperty.call(taxes, 'transferred_total')) {
      const payloadTransferred = Number(taxes.transferred_total || 0);
      return Number.isFinite(payloadTransferred) ? payloadTransferred : 0;
    }

    const explicit = this.concepts.reduce((sum, concept) => {
      const conceptTransferred = Number(concept?.taxTransferredTotal || 0);
      if (Number.isFinite(conceptTransferred) && conceptTransferred > 0) {
        return sum + conceptTransferred;
      }

      return sum;
    }, 0);

    if (explicit > 0) {
      return explicit;
    }

    return sumTaxItemsByDirection(this.concepts, 'trasladado');
  },
  withheldTaxAmount() {
    const taxes = this.invoiceData?.taxes;
    if (taxes && Object.prototype.hasOwnProperty.call(taxes, 'withheld_total')) {
      const payloadWithheld = Number(taxes.withheld_total || 0);
      return Number.isFinite(payloadWithheld) ? payloadWithheld : 0;
    }

    const explicit = this.concepts.reduce((sum, concept) => {
      const conceptWithheld = Number(concept?.taxWithheldTotal || 0);
      if (Number.isFinite(conceptWithheld) && conceptWithheld > 0) {
        return sum + conceptWithheld;
      }

      return sum;
    }, 0);

    if (explicit > 0) {
      return explicit;
    }

    return sumTaxItemsByDirection(this.concepts, 'retenido');
  },
  total() {
    const payloadTotal = Number(this.invoiceData?.total || 0);
    if (Number.isFinite(payloadTotal) && payloadTotal > 0) {
      return payloadTotal;
    }

    return this.subtotal - this.discountAmount + this.transferredTaxAmount - this.withheldTaxAmount;
  },
  invoiceOperation() {
    const concepts = Array.isArray(this.concepts) ? this.concepts : [];
    const invoiceType = String(this.invoiceData?.invoice_type || this.invoiceData?.type || '').trim().toLowerCase();
    const isGenericInvoice = invoiceType === 'generic';
    const serial = String(this.invoiceData?.serial || '').trim();
    const folio = String(this.invoiceData?.folio || '').trim();
    const invoiceReference = `${serial}${folio}`.trim() || String(this.text?.toasts?.invoiceReferenceFallback || 'Factura').trim() || 'Factura';
    const explicitBalance = Number(this.invoiceData?.balance);
    const hasBalance = Number.isFinite(explicitBalance);
    const normalizedBalance = hasBalance ? explicitBalance : null;

    const rawStatusKey = String(
      this.invoiceData?.status_key
      || this.invoiceData?.statusKey
      || this.invoiceData?.payment_status_code
      || ''
    ).trim().toLowerCase();

    const rawStatusLabel = String(
      this.invoiceData?.status
      || this.invoiceData?.payment_status
      || this.invoiceData?.paymentStatus
      || ''
    ).trim().toLowerCase();

    let statusKey = rawStatusKey;
    if (!['done', 'paid', 'partial', 'pending', 'canceled'].includes(statusKey)) {
      if (rawStatusLabel.includes('cancel')) {
        statusKey = 'canceled';
      } else if (rawStatusLabel.includes('parcial')) {
        statusKey = 'partial';
      } else if (rawStatusLabel.includes('pagad') || rawStatusLabel === 'done') {
        statusKey = 'done';
      } else if (hasBalance && normalizedBalance <= 0) {
        statusKey = 'done';
      } else {
        statusKey = 'pending';
      }
    }

    const statusLabels = this.text?.table?.statusPills || {};
    const statusLabelMap = {
      done: String(statusLabels.paid || 'Pagada'),
      paid: String(statusLabels.paid || 'Pagada'),
      partial: String(statusLabels.partial || 'Parcialmente pagada'),
      pending: String(statusLabels.pending || 'Pendiente'),
      canceled: String(statusLabels.canceled || 'Cancelada'),
    };
    const statusLabel = statusLabelMap[statusKey] || String(statusLabels.pending || 'Pendiente');

    const conceptPaymentId = concepts
      .map((concept) => Number(concept?.paymentId || concept?.payment_id || 0) || 0)
      .find((paymentId) => paymentId > 0) || 0;
    const paymentId = Number(this.invoiceData?.payment_id || this.invoiceData?.payment?.id || conceptPaymentId || 0) || 0;
    const paymentReceiptId = Number(this.invoiceData?.payment_receipt_id || 0) || 0;
    const paymentReceiptCode = String(
      this.invoiceData?.payment_receipt_code
      || this.invoiceData?.payment?.code
      || ''
    ).trim();
    const paymentReceiptCodes = Array.isArray(this.invoiceData?.payment_receipt_codes)
      ? this.invoiceData.payment_receipt_codes
        .map((code) => String(code || '').trim())
        .filter((code) => code !== '')
      : [];
    const paymentReceiptCodesText = String(this.invoiceData?.payment_receipt_codes_text || '').trim();
    const paymentReference = String(
      this.invoiceData?.payment_reference
      || this.invoiceData?.payment?.reference
      || this.invoiceData?.payment?.code
      || ''
    ).trim();
    const hasRelatedPayment = paymentId > 0 || paymentReceiptId > 0 || paymentReference !== '';
    const relatedPaymentLabel = paymentReceiptCodes.length > 0
      ? paymentReceiptCodes.join(', ')
      : (paymentReceiptCodesText !== ''
        ? paymentReceiptCodesText
        : (paymentReceiptCode !== ''
          ? paymentReceiptCode
          : ''));

    const saleIdFromInvoice = Number(this.invoiceData?.sale_id || this.invoiceData?.saleId || 0) || 0;
    const saleIdFromConcept = concepts
      .map((concept) => Number(concept?.saleId || concept?.sale_id || 0) || 0)
      .find((saleId) => saleId > 0) || 0;

    const customerId = Number(
      this.invoiceData?.company_id
      || this.invoiceData?.customer_id
      || 0
    ) || 0;

    const sentRaw = this.invoiceData?.has_received_proof;
    const sent = sentRaw === true || ['1', 'true', 'yes', 'si', 'sí'].includes(String(sentRaw ?? '').trim().toLowerCase());

    return {
      statusKey,
      statusLabel,
      relatedPaymentLabel,
      paymentId,
      paymentReceiptId,
      balance: normalizedBalance,
      dueDate: String(this.invoiceData?.due_date || this.invoiceData?.dueDate || this.invoiceData?.date || '').trim(),
      customerId,
      customerName: String(
        this.invoiceData?.customer_name
        || this.invoiceData?.company_name
        || ''
      ).trim(),
      hasGenericInvoice: isGenericInvoice,
      saleId: isGenericInvoice ? 0 : (saleIdFromInvoice > 0 ? saleIdFromInvoice : saleIdFromConcept),
      saleName: String(
        this.invoiceData?.sale_name
        || this.invoiceData?.saleName
        || this.invoiceData?.business_name
        || this.invoiceData?.businessName
        || ''
      ).trim(),
      saleCode: String(this.invoiceData?.sale_code || this.invoiceData?.saleCode || '').trim(),
      sent,
      lastSendAt: String(
        this.invoiceData?.last_sent_at
        || this.invoiceData?.lastSendAt
        || this.invoiceData?.sent_at
        || ''
      ).trim(),
      currency: this.currency,
      paymentReceiptCode,
      paymentReceiptCodesText: relatedPaymentLabel,
      invoiceReference,
    };
  },
};
